/**
 * CSS Variables Generation
 *
 * Generates CSS custom properties from the Color Registry.
 * Enables runtime theming and tower-specific color switching.
 *
 * @example
 * ```css
 * :root {
 *   --color-primary: #00a651;
 *   --color-secondary: #7cb342;
 * }
 *
 * .tower-vet {
 *   --color-primary: #0072CE;
 * }
 * ```
 */

import type {CSSProperties} from 'react'
import {colorRegistry, type ColorInfo} from './registry'

// ============================================================================
// TYPES
// ============================================================================

export interface CSSVariableSet {
	variableName: string
	value: string
}

// ============================================================================
// CSS VARIABLE GENERATION
// ============================================================================

/**
 * Generate CSS variable name from color ID
 */
export function getCSSVariableName(colorId: string): string {
	return `--color-${colorId.replace(/_/g, '-')}`
}

/**
 * Generate all CSS variables from registry
 */
export function generateCSSVariables(): CSSVariableSet[] {
	return Object.values(colorRegistry).map((color: ColorInfo) => ({
		variableName: getCSSVariableName(color.id),
		value: color.hexCode,
	}))
}

/**
 * Generate CSS :root block with all color variables
 */
export function generateRootCSS(): string {
	const variables = generateCSSVariables()
	const lines = variables.map((v) => `  ${v.variableName}: ${v.value};`)
	return `:root {\n${lines.join('\n')}\n}`
}

/**
 * Generate complete CSS file with all variables
 */
export function generateCompleteCSSFile(): string {
	const sections: string[] = [
		'/* Auto-generated from Color Registry - DO NOT EDIT MANUALLY */',
		`/* Generated: ${new Date().toISOString()} */`,
		'',
		'/* Base colors */',
		generateRootCSS(),
	]

	return sections.filter(Boolean).join('\n')
}

// ============================================================================
// INLINE STYLE HELPERS
// ============================================================================

/**
 * Get inline style object for a color
 */
export function getColorStyle(colorId: string): CSSProperties {
	const color = colorRegistry[colorId]
	if (!color) return {}

	return {
		backgroundColor: color.hexCode,
	}
}

/**
 * Get CSS variable reference for use in styles
 */
export function getColorVar(colorId: string): string {
	return `var(${getCSSVariableName(colorId)})`
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert hex to RGB values (for rgba usage)
 */
export function hexToRGB(
	hex: string
): {r: number; g: number; b: number} | null {
	const hexPattern =
		/^#?(?<red>[a-f\d]{2})(?<green>[a-f\d]{2})(?<blue>[a-f\d]{2})$/i
	const result = hexPattern.exec(hex)
	if (!result?.groups) return null

	const {red, green, blue} = result.groups
	if (!red || !green || !blue) return null

	return {
		r: Number.parseInt(red, 16),
		g: Number.parseInt(green, 16),
		b: Number.parseInt(blue, 16),
	}
}

/**
 * Generate RGB CSS variable (for opacity support)
 */
export function generateRGBVariable(colorId: string, hex: string): string {
	const rgb = hexToRGB(hex)
	if (!rgb) return ''
	return `  --color-${colorId}-rgb: ${rgb.r}, ${rgb.g}, ${rgb.b};`
}
